import type { Knex } from 'knex'
import { logger } from '@kernel/logger/index.js'

/**
 * Check if a process is running.
 */
function isProcessRunning(pid: number): boolean {
  try {
    // Signal 0 doesn't kill the process, just checks if it exists
    process.kill(pid, 0)
    return true
  } catch {
    return false
  }
}

/**
 * Acquire migration lock.
 * Prevents concurrent migration executions.
 */
export async function acquireMigrationLock(
  knex: Knex,
  options: { timeout?: number } = {}
): Promise<void> {
  const timeout = options.timeout ?? 60000
  const startTime = Date.now()
  const _log = logger

  while (true) {
    try {
      // Try to acquire lock with SELECT FOR UPDATE
      const acquired = await knex.transaction(async (trx) => {
        const row = await trx('_nexus_migration_lock').where({ id: 1 }).forUpdate().first()

        if (!row) {
          // Create lock for the first time
          await trx('_nexus_migration_lock').insert({
            id: 1,
            is_locked: true,
            locked_at: trx.fn.now(),
            locked_by: String(process.pid),
          })
          return true
        }

        if (!row.is_locked) {
          // Lock available
          await trx('_nexus_migration_lock').where({ id: 1 }).update({
            is_locked: true,
            locked_at: trx.fn.now(),
            locked_by: String(process.pid),
          })
          return true
        }

        // Lock occupied - check if process is still alive
        const lockedPid = parseInt(row.locked_by ?? '0', 10)
        if (lockedPid && !isProcessRunning(lockedPid)) {
          // Process died - release lock automatically
          logger.warn({ pid: lockedPid }, 'Stale lock detected, releasing')
          await trx('_nexus_migration_lock').where({ id: 1 }).update({
            is_locked: false,
            locked_by: null,
          })
          return false // Retry
        }

        return false
      })

      if (acquired) {
        logger.debug({ pid: process.pid }, 'Migration lock acquired')
        return
      }
    } catch (err) {
      // Lock timeout or error - will retry
      logger.debug({ err }, 'Failed to acquire lock, retrying')
    }

    // Check timeout before sleeping to avoid unnecessary delay
    if (Date.now() - startTime >= timeout) {
      throw new Error(
        'Migration lock timeout - another process is running migrations. ' +
          'If no other process is running, check for stale locks in _nexus_migration_lock table.'
      )
    }

    // Wait before retrying
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }
}

/**
 * Release migration lock.
 */
export async function releaseMigrationLock(knex: Knex): Promise<void> {
  const _log = logger

  try {
    await knex('_nexus_migration_lock').where({ id: 1 }).update({
      is_locked: false,
      locked_by: null,
    })

    logger.debug({ pid: process.pid }, 'Migration lock released')
  } catch (err) {
    logger.error({ err }, 'Failed to release migration lock')
    // Don't throw - we're likely in a finally block
  }
}
